import { SyntheticEvent, useCallback, useState } from 'react';
import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ChangeUser } from 'features/user';

import { DiseaseExplainDrawer, SubjectCard, userLib, userModel } from 'entities/user';

import { LoadingProgress } from 'shared/ui';

type SubjectsTab = 'people' | 'pets';

const parseTab = (raw: string | null): SubjectsTab => (raw === 'pets' ? 'pets' : 'people');

export const User = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { isFetching } = userModel.getUserByIdAsync(id as string)(dispatch);
  const { selectedUser } = userModel.useUser();
  const [explainTarget, setExplainTarget] = useState<userModel.ExplainTarget | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));

  const handleTabChange = useCallback(
    (_: SyntheticEvent, value: SubjectsTab) => {
      setSearchParams(value === 'pets' ? { tab: 'pets' } : {}, { replace: true });
    },
    [setSearchParams],
  );

  const handleBack = () => {
    dispatch(userModel.setSelectedUser(null));
    navigate(-1);
  };

  if (isFetching) return <LoadingProgress />;

  const peoples = selectedUser?.peoples ?? [];
  const pets = selectedUser?.pets ?? [];

  return (
    <Box display="flex" flexDirection="column" flex={1} px={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button startIcon={<ArrowBack />} onClick={handleBack}>
          Back
        </Button>
      </Box>
      <Typography variant="h5" fontWeight="bold">
        User: {selectedUser?.email}
        <ChangeUser user={selectedUser} />
      </Typography>
      <Typography variant="subtitle1" fontWeight="bold" mb={3}>
        Role: {userLib.getUserRoleText(selectedUser?.role)}
      </Typography>
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        {selectedUser?.descriptions && (
          <>
            <Typography variant="subtitle1" fontWeight="bolder">
              Comments about user
            </Typography>
            <Paper sx={{ p: 1 }}>{selectedUser?.descriptions}</Paper>
          </>
        )}
      </Box>
      {selectedUser && (
        <>
          <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)', borderRadius: '8px', width: 'fit-content', mt: 3, mb: 2 }}>
            <Tabs value={tab} onChange={handleTabChange}>
              <Tab value="people" label={`People (${peoples.length})`} />
              <Tab value="pets" label={`Pets (${pets.length})`} />
            </Tabs>
          </Box>
          {tab === 'people' &&
            (peoples.length !== 0 ? (
              <Box display="flex" gap={2} flexWrap="wrap">
                {peoples.map((people) => (
                  <SubjectCard key={people._id} kind="people" subject={people} onDiseaseClick={setExplainTarget} />
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">No people</Typography>
            ))}
          {tab === 'pets' &&
            (pets.length !== 0 ? (
              <Box display="flex" gap={2} flexWrap="wrap">
                {pets.map((pet) => (
                  <SubjectCard key={pet._id} kind="pet" subject={pet} onDiseaseClick={setExplainTarget} />
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">No pets</Typography>
            ))}
        </>
      )}
      <DiseaseExplainDrawer target={explainTarget} onClose={() => setExplainTarget(null)} />
    </Box>
  );
};
